import { FormEvent, useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { supabase } from "./lib/supabase";
import { VehicleChannelPublication } from "./VehicleChannelPublication";
import "./vehicle-marketing.css";

type MarketingLink = {
  id: string;
  code: string;
  channel: string;
  campaign_name: string | null;
  destination_url: string;
  active: boolean;
  click_count: number;
  last_clicked_at: string | null;
  created_at: string;
};

const channels: Record<string, string> = {
  web_chat: "Chat do site",
  whatsapp: "WhatsApp",
  email: "Email",
  phone: "Telefone",
  form: "Formulário",
  paid_ad: "Anúncio pago",
  vehicle_portal: "Portal automóvel",
  other: "Outro canal",
};

const trackingUrl = (code: string) => `${window.location.origin}/m/${code}`;
const readableDate = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat("pt-PT", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value))
    : "Ainda sem acessos";

export function VehicleMarketingPanel({
  vehicleId,
  organizationId,
  writable,
}: {
  vehicleId: string;
  organizationId: string;
  writable: boolean;
}) {
  const [links, setLinks] = useState<MarketingLink[]>([]),
    [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<MarketingLink | null>(null),
    [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false),
    [feedback, setFeedback] = useState<{
      kind: "success" | "error";
      message: string;
    } | null>(null);
  const [qrCodes, setQrCodes] = useState<Record<string, string>>({});
  const activeCount = useMemo(
    () => links.filter((item) => item.active).length,
    [links],
  );
  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("vehicle_marketing_links")
      .select(
        "id,code,channel,campaign_name,destination_url,active,click_count,last_clicked_at,created_at",
      )
      .eq("organization_id", organizationId)
      .eq("vehicle_id", vehicleId)
      .order("created_at", { ascending: false });
    setLoading(false);
    if (error) return setFeedback({ kind: "error", message: error.message });
    const rows = (data ?? []) as MarketingLink[];
    setLinks(rows);
    const generated = await Promise.all(
      rows.map(
        async (item) =>
          [
            item.id,
            await QRCode.toDataURL(trackingUrl(item.code), {
              width: 240,
              margin: 1,
              color: { dark: "#07120f", light: "#ffffff" },
            }),
          ] as const,
      ),
    );
    setQrCodes(Object.fromEntries(generated));
  };
  useEffect(() => {
    void load();
  }, [vehicleId, organizationId]);
  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 10000);
    return () => window.clearTimeout(timer);
  }, [feedback]);
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setFeedback(null);
    const form = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("save_vehicle_marketing_link", {
      p_vehicle_id: vehicleId,
      p_channel: String(form.get("channel")),
      p_campaign_name: String(form.get("campaign_name") || ""),
      p_destination_url: String(form.get("destination_url") || ""),
      p_link_id: editing?.id ?? null,
    });
    setBusy(false);
    if (error) return setFeedback({ kind: "error", message: error.message });
    setEditing(null);
    setCreating(false);
    setFeedback({ kind: "success", message: "Identificador guardado." });
    await load();
  };
  const toggle = async (item: MarketingLink) => {
    setBusy(true);
    const { error } = await supabase.rpc("set_vehicle_marketing_link_active", {
      p_link_id: item.id,
      p_active: !item.active,
    });
    setBusy(false);
    if (error) return setFeedback({ kind: "error", message: error.message });
    setFeedback({
      kind: "success",
      message: item.active
        ? "Identificador colocado em pausa."
        : "Identificador reativado.",
    });
    await load();
  };
  const copy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setFeedback({ kind: "success", message: `${label} copiado.` });
  };
  return (
    <section className="vehicle-marketing">
      <header>
        <div>
          <p className="eyebrow">Marketing da viatura</p>
          <h3>Origens e canais</h3>
          <p>
            Cada ligação reconhece esta viatura, o canal e a campanha que
            originaram o contacto.
          </p>
        </div>
        {writable && (
          <button
            type="button"
            className="stock-primary"
            onClick={() => {
              setEditing(null);
              setCreating(true);
            }}
          >
            Novo identificador
          </button>
        )}
      </header>
      {feedback && (
        <button
          type="button"
          className={`marketing-feedback ${feedback.kind}`}
          onClick={() => setFeedback(null)}
        >
          {feedback.message} ×
        </button>
      )}
      <div className="marketing-summary">
        <article>
          <small>Identificadores ativos</small>
          <strong>{activeCount}</strong>
        </article>
        <article>
          <small>Total de acessos</small>
          <strong>
            {links.reduce((sum, item) => sum + Number(item.click_count), 0)}
          </strong>
        </article>
      </div>
      {loading ? (
        <p className="marketing-empty">A carregar identificadores…</p>
      ) : links.length === 0 ? (
        <div className="marketing-empty">
          <strong>Ainda não existem identificadores.</strong>
          <span>
            Crie o primeiro para divulgar esta viatura com origem rastreável.
          </span>
        </div>
      ) : (
        <div className="marketing-list">
          {links.map((item) => (
            <article key={item.id} className={!item.active ? "paused" : ""}>
              <img
                src={qrCodes[item.id]}
                alt={`QR code ${channels[item.channel]}`}
              />
              <div className="marketing-info">
                <span className="marketing-channel">
                  {channels[item.channel] ?? item.channel}
                </span>
                <strong>
                  {item.campaign_name || "Ligação geral da viatura"}
                </strong>
                <code>{trackingUrl(item.code)}</code>
                <small>
                  {item.click_count} acessos · último:{" "}
                  {readableDate(item.last_clicked_at)}
                </small>
                <small>Destino: {item.destination_url}</small>
              </div>
              <div className="marketing-actions">
                <button
                  type="button"
                  onClick={() => void copy(trackingUrl(item.code), "Ligação")}
                >
                  Copiar ligação
                </button>
                <button
                  type="button"
                  onClick={() => void copy(item.code, "Identificador")}
                >
                  Copiar identificador
                </button>
                {qrCodes[item.id] && (
                  <a
                    href={qrCodes[item.id]}
                    download={`vorno-${item.code}.png`}
                  >
                    Guardar QR
                  </a>
                )}
                {writable && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(item);
                        setCreating(true);
                      }}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void toggle(item)}
                    >
                      {item.active ? "Pausar" : "Reativar"}
                    </button>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      {creating && (
        <div className="marketing-dialog-backdrop">
          <form className="marketing-dialog" onSubmit={save}>
            <header>
              <div>
                <p className="eyebrow">
                  {editing ? "Editar identificador" : "Novo identificador"}
                </p>
                <h3>
                  {editing?.campaign_name || "Definir origem do contacto"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                }}
              >
                ×
              </button>
            </header>
            <div>
              <label>
                <span>Canal *</span>
                <select
                  name="channel"
                  required
                  defaultValue={editing?.channel || "whatsapp"}
                >
                  {Object.entries(channels).map(([value, label]) => (
                  <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Campanha</span>
                <input
                  name="campaign_name"
                  maxLength={120}
                  defaultValue={editing?.campaign_name || ""}
                  placeholder="Ex.: Facebook · Volvo XC40 · Agosto"
                />
              </label>
              <label className="wide">
                <span>Destino *</span>
                <input
                  name="destination_url"
                  required
                  defaultValue={editing?.destination_url || ""}
                  placeholder="https://…, mailto:… ou tel:…"
                />
                <small>
                  É o local aberto depois de o sistema reconhecer a origem.
                </small>
              </label>
            </div>
            <footer>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                }}
              >
                Cancelar
              </button>
              <button className="stock-primary" disabled={busy}>
                {busy ? "A guardar…" : "Guardar"}
              </button>
            </footer>
          </form>
        </div>
      )}
      <VehicleChannelPublication vehicleId={vehicleId} />
    </section>
  );
}
